import { useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import Calendar from './Calendar';
import { WEEKDAY_SHORT, addDays, longDate, parseISODate, relativeDayLabel, today, weekDays } from '../lib/dates';
import { useApp } from '../lib/AppContext';

const ICON_BUTTON = 'btn btn-circle btn-ghost size-11';

// "Hôm nay" over "Thứ Bảy, 24/10", previous/next-week and date-picker buttons, then the seven days T2–CN:
// a green dot means logged, a grey dot means missed, the selected day is dark and future days are dashed. Future
// days open too (their scheduled sessions); DayPage keeps them from being logged.
export default function WeekStrip({ date, onChange, selectedLogged }) {
  const { api } = useApp();
  const days = weekDays(date);
  const todayIso = today();
  const [logged, setLogged] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLogged(null);
    api.get(`/stats?${new URLSearchParams({ from: days[0], to: days[6], group: 'day' })}`)
      .then((data) => !cancelled && setLogged(Object.fromEntries(data.series.map((r) => [r.period, r.logged]))))
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api, days[0]]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // The open day's dot follows what was just logged, without waiting for the week to reload. Until the
  // week has loaded (null), no dots at all rather than grey "missed" ones.
  const isLogged = (iso) => (iso === date && selectedLogged != null ? selectedLogged : logged?.[iso]);
  const go = (iso) => { setOpen(false); onChange(iso); };
  const label = relativeDayLabel(date);

  return (
    <div ref={ref} className="relative flex flex-col gap-3">
      <div className="flex items-end justify-between gap-2">
        <div>
          <div className="text-sm text-muted">{['Hôm nay', 'Hôm qua', 'Ngày mai'].includes(label) ? label : parseISODate(date).getFullYear()}</div>
          <h1 className="text-[22px] font-bold leading-tight">{longDate(date)}</h1>
        </div>
        <div className="flex items-center">
          <button type="button" className={ICON_BUTTON} aria-label="Tuần trước" onClick={() => go(addDays(date, -7))}>
            <Icon name="chevronLeft" />
          </button>
          <button type="button" className={ICON_BUTTON} aria-label="Tuần sau" onClick={() => go(addDays(date, 7))}>
            <Icon name="chevronRight" />
          </button>
          <button type="button" className={ICON_BUTTON} aria-label="Chọn ngày" aria-expanded={open} aria-haspopup="dialog"
            onClick={() => setOpen((o) => !o)}>
            <Icon name="calendar" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {days.map((iso, i) => {
          const selected = iso === date;
          const future = iso > todayIso;
          const done = isLogged(iso);
          const missed = logged != null && !done && !future && iso !== todayIso;
          const name = parseISODate(iso).toLocaleDateString('vi-VN', { weekday: 'long', day: 'numeric', month: 'numeric' });
          return (
            <button key={iso} type="button" onClick={() => go(iso)}
              aria-pressed={selected} aria-current={iso === todayIso ? 'date' : undefined}
              aria-label={`${name}${future ? ', chưa tới' : done ? ', đã ghi' : ', chưa ghi'}`}
              className={`flex h-[62px] flex-col items-center justify-center gap-0.5 rounded-[14px] border transition
                ${selected ? 'border-neutral bg-neutral text-neutral-content' : 'border-line bg-base-100'}
                ${future && !selected ? 'border-dashed bg-transparent text-muted' : ''}
                ${iso === todayIso && !selected ? 'border-primary' : ''}`}>
              <span className={`text-[11px] ${selected ? 'text-neutral-content/80' : 'text-muted'}`}>{WEEKDAY_SHORT[i]}</span>
              <span className="tabular text-[17px] font-bold leading-none">{parseISODate(iso).getDate()}</span>
              <span className={`size-1.5 rounded-full ${done ? (selected ? 'bg-[#6fd3a0]' : 'bg-primary') : missed ? 'bg-[#a3ada7]' : 'bg-transparent'}`} />
            </button>
          );
        })}
      </div>

      {open && (
        <div role="dialog" aria-label="Chọn ngày"
          className="absolute inset-x-0 top-14 z-30 rounded-[20px] border border-line bg-base-100 p-3 shadow-xl">
          <Calendar value={date} onSelect={go} showLog />
        </div>
      )}
    </div>
  );
}
